import React, { useEffect, useState } from 'react';
import './index.scss';

export interface ToastMessage {
  id: string;
  duration: number;
  content: React.ReactNode;
}

interface ToastProps {
  message: ToastMessage;
  onRemove: (id: string) => void;
}

const Toast: React.FC<ToastProps> = ({ message, onRemove }) => {
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsExiting(true);
      setTimeout(() => {
        onRemove(message.id);
      }, 300);
    }, message.duration);
    return () => {
      clearTimeout(timer);
    };
  }, [message.id, message.duration, onRemove]);

  return (
    <div
      className={`toast-item bg-up-black-80 text-white py-1.5 px-3 mx-4 rounded-xl text-sm font-normal mb-4 break-words text-center pointer-events-auto ${isExiting ? 'toast-exit' : 'toast-enter'}`}>
      {message.content}
    </div>
  );
};

export const ToastContainer: React.FC = () => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  useEffect(() => {
    const handleToast = (event: Event) => {
      const customEvent = event as CustomEvent<ToastMessage>;
      setToasts(prev => [...prev, customEvent.detail]);
    };
    window.addEventListener('show-toast', handleToast);
    return () => {
      window.removeEventListener('show-toast', handleToast);
    };
  }, []);

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  return (
    <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-99 flex flex-col items-center pointer-events-none w-full">
      {toasts.map(toast => (
        <Toast key={toast.id} message={toast} onRemove={removeToast} />
      ))}
    </div>
  );
};

let toastId = 0;

const showToast = (content: React.ReactNode, duration = 3000) => {
  const id = `toast-${String(toastId++)}`;
  const event = new CustomEvent('show-toast', {
    detail: { id, content, duration },
  });
  window.dispatchEvent(event);
};

// eslint-disable-next-line react-refresh/only-export-components
export const toast = {
  info: (content: React.ReactNode, duration = 3000) => {
    showToast(content, duration);
  },
  success: (content: React.ReactNode, duration = 3000) => {
    showToast(content, duration);
  },
  error: (content: React.ReactNode, duration = 3000) => {
    showToast(content, duration);
  },
  warning: (content: React.ReactNode, duration = 3000) => {
    showToast(content, duration);
  },
};
